import { Link } from "react-router-dom";
import type { VideoItem } from "@/mocks/videos";
import { categoryMeta } from "@/mocks/videos";
import { useFavorites } from "@/hooks/useFavorites";

interface VideoCardProps {
  video: VideoItem;
  showFavorite?: boolean;
}

export default function VideoCard({ video, showFavorite = true }: VideoCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const fav = isFavorite(video.id);

  const handleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(video.id);
  };

  return (
    <Link
      to={`/detail/${video.id}`}
      className="group block cursor-pointer overflow-hidden rounded-lg bg-background-50 transition-transform duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
        <img
          src={video.cover}
          alt={`${video.title} 封面`}
          title={`${video.title} 在线观看`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent opacity-70"></div>
        <span className="absolute left-2 top-2 rounded-md bg-foreground-950/70 px-2 py-1 text-[11px] font-medium text-background-50 backdrop-blur-sm">
          {video.status}
        </span>
        <span className="absolute right-2 top-2 rounded-md bg-accent-500 px-2 py-1 text-[11px] font-bold text-background-50">
          {video.rating.toFixed(1)}
        </span>
        {showFavorite && (
          <button
            type="button"
            onClick={handleFavorite}
            className={`absolute bottom-2 right-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full backdrop-blur-sm transition-colors ${
              fav
                ? "bg-primary-500 text-background-50"
                : "bg-foreground-950/50 text-background-50 hover:bg-primary-500"
            }`}
            aria-label={fav ? "取消收藏" : "收藏"}
          >
            <i className={`${fav ? "ri-heart-3-fill" : "ri-heart-3-line"} text-base`}></i>
          </button>
        )}
        <div className="absolute bottom-2 left-2 flex flex-wrap gap-1">
          <span className="rounded bg-background-50/90 px-1.5 py-0.5 text-[10px] font-medium text-foreground-800">
            {categoryMeta[video.category].name}
          </span>
          {video.genre.slice(0, 1).map((g) => (
            <span
              key={g}
              className="rounded bg-primary-500/90 px-1.5 py-0.5 text-[10px] font-medium text-background-50"
            >
              {g}
            </span>
          ))}
        </div>
      </div>
      <div className="px-0.5 pt-2.5">
        <h3 className="line-clamp-2 text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
          {video.title}
        </h3>
        <p className="mt-1 text-xs text-foreground-500">
          {video.year} · {video.region}
        </p>
      </div>
    </Link>
  );
}